import type { FieldErrors } from 'react-hook-form';

import {
  BANNER_LANGUAGES,
  BANNER_TRANSLATION_LANGUAGES,
  type BannerFormValues,
  type BannerLanguage,
  type BannerPortugueseGap,
} from '../types/bannerForm';

/**
 * Âncoras `data-field-error` do formulário de Banner — o contêiner do campo (ou
 * da seção) que a tela rola até quando a validação barra o salvamento.
 */
export const BANNER_ERROR_ANCHORS = {
  Title: 'title',
  StartDate: 'startDate',
  EndDate: 'endDate',
  Url: 'url',
  Duration: 'duration',
  ClassStudents: 'classStudents',
  Media: 'media',
  /** Campo da arte do ambiente legado, no fim da seção Mídia. */
  ImageDesktop: 'imageDesktop',
  TranslationUrl: 'translationUrl',
} as const;

/** Âncora de um campo com erro. */
export type BannerErrorAnchor = (typeof BANNER_ERROR_ANCHORS)[keyof typeof BANNER_ERROR_ANCHORS];

/** Campo com erro a que a tela deve levar o operador. */
export interface BannerErrorTarget {
  readonly anchor: BannerErrorAnchor;
  /** Aba de idioma em que o campo está — trocar de aba vem antes de rolar. */
  readonly language: BannerLanguage;
}

/**
 * Ordem de leitura dos campos do cadastro em português (topo → base).
 *
 * É ela que decide qual erro é o "primeiro": o objeto de erros do React Hook
 * Form não tem ordem que corresponda ao layout da tela. Mídia é uma seção só,
 * então `videoFile` e `imageFile` compartilham a mesma âncora.
 */
const PORTUGUESE_ERROR_ORDER: readonly {
  readonly field: keyof BannerFormValues;
  readonly anchor: BannerErrorAnchor;
}[] = [
  { field: 'title', anchor: BANNER_ERROR_ANCHORS.Title },
  { field: 'startDate', anchor: BANNER_ERROR_ANCHORS.StartDate },
  { field: 'endDate', anchor: BANNER_ERROR_ANCHORS.EndDate },
  { field: 'url', anchor: BANNER_ERROR_ANCHORS.Url },
  { field: 'duration', anchor: BANNER_ERROR_ANCHORS.Duration },
  { field: 'classStudents', anchor: BANNER_ERROR_ANCHORS.ClassStudents },
  { field: 'videoFile', anchor: BANNER_ERROR_ANCHORS.Media },
  { field: 'imageFile', anchor: BANNER_ERROR_ANCHORS.Media },
  { field: 'imageDesktopFile', anchor: BANNER_ERROR_ANCHORS.ImageDesktop },
];

/**
 * Primeiro campo inválido do formulário de Banner, na ordem em que aparece na
 * tela.
 *
 * O cadastro em português vem antes das traduções — inclusive quando o erro é o
 * "preencha o português primeiro" (`translations` sem `path` de idioma), que
 * aponta para a lacuna do próprio português.
 *
 * @param errors - Erros do React Hook Form.
 * @param portugueseGaps - Lacunas do cadastro em português (ver `bannerPortugueseGaps`).
 * @returns Âncora e aba de idioma do primeiro erro; `null` quando não há erro conhecido.
 *
 * @example
 * ```ts
 * const target = resolveFirstBannerErrorTarget(errors, gaps);
 * // → { anchor: 'title', language: 'pt-BR' }
 * ```
 */
export function resolveFirstBannerErrorTarget(
  errors: FieldErrors<BannerFormValues>,
  portugueseGaps: readonly BannerPortugueseGap[],
): BannerErrorTarget | null {
  for (const { field, anchor } of PORTUGUESE_ERROR_ORDER) {
    if (errors[field]) return { anchor, language: BANNER_LANGUAGES.PtBr };
  }

  if (errors.translations?.message !== undefined) {
    return {
      anchor:
        portugueseGaps[0] === 'media' ? BANNER_ERROR_ANCHORS.Media : BANNER_ERROR_ANCHORS.Title,
      language: BANNER_LANGUAGES.PtBr,
    };
  }

  for (const culture of BANNER_TRANSLATION_LANGUAGES) {
    if (errors.translations?.[culture]?.url) {
      return { anchor: BANNER_ERROR_ANCHORS.TranslationUrl, language: culture };
    }
  }

  return null;
}

/**
 * Rola a página até a âncora do campo com erro e foca o controle interno.
 *
 * Chamada depois do commit do React: se o erro está em outra aba de idioma, a
 * aba já precisa ter sido trocada — a âncora só existe no DOM da aba visível.
 *
 * @param anchor - Âncora `data-field-error` do campo.
 */
export function scrollToBannerError(anchor: BannerErrorAnchor): void {
  const element = document.querySelector<HTMLElement>(`[data-field-error="${anchor}"]`);
  if (!element) return;

  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // O input de arquivo fica escondido atrás do card de upload: focá-lo não
  // levaria o cursor a lugar nenhum útil.
  element
    .querySelector<HTMLElement>('input:not([type="file"]), textarea, select')
    ?.focus({ preventScroll: true });
}
